import * as React from "react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { DevToolbar } from "@/components/DevToolbar";
import { isLocalDev } from "@/lib/is-local-dev";

interface NavItem {
  label: string;
  href: string;
}

interface NavbarProps {
  logo?: React.ReactNode;
  companyName?: string;
  navItems?: NavItem[];
  ctaText?: string;
}

const defaultNavItems: NavItem[] = [
  { label: "Features", href: "#features" },
  { label: "Pricing", href: "#pricing" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "FAQ", href: "#faq" },
];

export function Navbar({
  logo,
  companyName = "Equip Scout",
  navItems = defaultNavItems,
  ctaText = "Get Started",
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav
      className={cn(
        "fixed top-0 left-0 right-0 z-50",
        "transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
        isScrolled
          ? "bg-background/80 backdrop-blur-lg border-b border-border shadow-sm"
          : "bg-transparent"
      )}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a
            href="#"
            className="flex items-center gap-2 text-foreground font-semibold text-lg"
          >
            {logo || (
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-white font-bold text-sm">ES</span>
              </div>
            )}
            <span className="hidden sm:block">{companyName}</span>
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">Beta</Badge>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => handleNavClick(item.href)}
                className={cn(
                  "px-4 py-2 rounded-lg text-sm font-medium",
                  "text-muted-foreground hover:text-foreground",
                  "hover:bg-muted",
                  "transition-colors duration-200"
                )}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Auth & CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <DevToolbar />
            <Link
              to="/login"
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium",
                "text-muted-foreground hover:text-foreground",
                "hover:bg-muted",
                "transition-colors duration-200"
              )}
            >
              Sign in
            </Link>
            <Link to={isLocalDev ? "/signup" : "/login"}>
              <Button className="rounded-full">
                {ctaText}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={cn(
              "md:hidden p-2 rounded-lg",
              "text-muted-foreground hover:text-foreground",
              "hover:bg-muted",
              "transition-colors duration-200"
            )}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={cn(
          "md:hidden overflow-hidden",
          "transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
          isMobileMenuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="bg-background border-t border-border">
          <div className="container mx-auto px-4 py-4 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => handleNavClick(item.href)}
                className={cn(
                  "block w-full text-left px-4 py-3 rounded-lg",
                  "text-muted-foreground hover:text-foreground",
                  "hover:bg-muted",
                  "transition-colors duration-200"
                )}
              >
                {item.label}
              </button>
            ))}
            <div className="px-4 py-2">
              <DevToolbar />
            </div>
            <Link
              to="/login"
              onClick={() => setIsMobileMenuOpen(false)}
              className={cn(
                "block w-full text-left px-4 py-3 rounded-lg",
                "text-muted-foreground hover:text-foreground",
                "hover:bg-muted",
                "transition-colors duration-200"
              )}
            >
              Sign in
            </Link>
            <div className="pt-2">
              <Link to={isLocalDev ? "/signup" : "/login"} onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full rounded-full">
                  {ctaText}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
